import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { MdOpenInNew } from 'react-icons/md';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Checkbox from '@/components/Checkbox';
import { Switch } from '@/components/ui/switch';
import { Tag } from '@/components/Tag';
import AvatarEditorModal from '@/features/agent/candidates/components/AvatarEditorModal';
import useFetchAllianceSetting from '../api/useFetchAllianceSetting';
import useFetchFormFields, { type FormFieldItem } from '../api/useFetchFormFields';
import useUpdateAllianceSetting from '../api/useUpdateAllianceSetting';
import useUpdateFormFields from '../api/useUpdateFormFields';
import useUploadAllianceLogo from '../api/useUploadAllianceLogo';
import useDeleteAllianceLogo from '../api/useDeleteAllianceLogo';
import useFetchAllianceAgentsMetadata from '../api/useFetchAllianceAgentsMetadata';
import { ConfirmDialogProvider, useConfirm } from '@/components/ConfirmDialogProvider';
import Loading from '@/components/Loading';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { getErrorMessage } from '../utils/getErrorMessage';

/** API から取得する app_base_url のフォールバック（取得前用） */
const DEFAULT_APP_BASE_URL = window.location.origin;

/** ロゴアップロード許可形式・上限（他画面のフォーム設定と同様） */
const LOGO_MAX_BYTES = 5 * 1024 * 1024;
const LOGO_ACCEPT = 'image/png,image/jpeg';
const LOGO_MIME = new Set(['image/png', 'image/jpeg']);

interface UpdateFormFieldItem {
  field_type: 'fixed' | 'custom';
  field_key?: string | null;
  custom_field_id?: number | null;
  is_required: boolean;
}

export default function AllianceSettings() {
  return (
    <ConfirmDialogProvider>
      <AllianceSettingsContent />
    </ConfirmDialogProvider>
  );
}

function AllianceSettingsContent() {
  const [allianceUrl, setAllianceUrl] = useState('');
  const [termsUrl, setTermsUrl] = useState('');
  const [formFields, setFormFields] = useState<FormFieldItem[]>([]);
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoOriginalUrl, setLogoOriginalUrl] = useState<string | null>(null);
  const [isLogoEditorOpen, setIsLogoEditorOpen] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const {
    data: allianceSetting,
    isLoading: isSettingLoading,
    error: settingError,
  } = useFetchAllianceSetting();
  const {
    data: formFieldsResponse,
    isLoading: isFormFieldsLoading,
    error: formFieldsError,
  } = useFetchFormFields();
  const updateAllianceSetting = useUpdateAllianceSetting();
  const updateFormFields = useUpdateFormFields();
  const uploadLogo = useUploadAllianceLogo();
  const deleteLogo = useDeleteAllianceLogo();
  const { data: agentsMetadata } = useFetchAllianceAgentsMetadata();
  const confirm = useConfirm();

  useEffect(() => {
    if (!allianceSetting) return;
    setAllianceUrl(allianceSetting.slug ?? '');
    setTermsUrl(allianceSetting.terms_of_service_url ?? '');
    if (allianceSetting.logo_url) {
      setLogoPreviewUrl(allianceSetting.logo_url);
    }
  }, [allianceSetting]);

  useEffect(() => {
    if (!formFieldsResponse) return;
    setFormFields(formFieldsResponse.items);
  }, [formFieldsResponse]);

  const getFieldId = (field: FormFieldItem) => {
    if (field.field_type === 'fixed') {
      return `fixed:${field.field_key}`;
    }
    return `custom:${field.custom_field_id}`;
  };

  const handleFieldToggle = (fieldId: string, checked: boolean) => {
    setFormFields((prev) =>
      prev.map((field) =>
        getFieldId(field) === fieldId
          ? { ...field, enabled: checked, ...(checked ? {} : { is_required: false }) }
          : field
      )
    );
  };

  const handleRequiredToggle = (fieldId: string, required: boolean) => {
    setFormFields((prev) =>
      prev.map((field) =>
        getFieldId(field) === fieldId ? { ...field, is_required: required } : field
      )
    );
  };

  /** 選択された画像でトリミングモーダルを開く（バリデーション済み想定） */
  const openLogoEditorWithFile = (file: File) => {
    const objectUrl = URL.createObjectURL(file);
    setLogoOriginalUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return objectUrl;
    });
    setIsLogoEditorOpen(true);
  };

  const trySelectLogoFile = (file: File) => {
    if (file.size > LOGO_MAX_BYTES) {
      showErrorToast('5MB以下の画像ファイルをアップロードしてください。');
      return;
    }
    if (!LOGO_MIME.has(file.type)) {
      showErrorToast('PNG または JPEG の画像を選択してください。');
      return;
    }
    openLogoEditorWithFile(file);
  };

  const handleLogoInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    trySelectLogoFile(file);
  };

  const base64ToBlob = (base64: string): Blob => {
    const [meta, data] = base64.split(',');
    const mimeMatch = meta.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const byteString = atob(data);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mime });
  };

  const handleGeneralSave = async () => {
    setActionErrorMessage(null);
    const trimmedSlug = allianceUrl?.trim();
    const trimmedTermsUrl = termsUrl?.trim();

    // slugが変更され、かつAllianceAgentが1人以上いる場合は確認ダイアログを表示
    const isSlugChanged = trimmedSlug !== (allianceSetting?.slug ?? '');
    if (isSlugChanged && agentsMetadata && agentsMetadata.current_count >= 1) {
      const confirmed = await confirm(
        'URLを変更すると、既存の提携エージェントのアクセスURLが変わります。よろしいですか？'
      );
      if (!confirmed) return;
    }

    updateAllianceSetting.mutate(
      {
        slug: trimmedSlug || null,
        terms_of_service_url: trimmedTermsUrl || null,
      },
      {
        onSuccess: (updatedSetting) => {
          setAllianceUrl(updatedSetting.slug ?? '');
          setTermsUrl(updatedSetting.terms_of_service_url ?? '');
          showSuccessToast('全般設定を保存しました');
        },
        onError: (error) => {
          const message = getErrorMessage(error, '全般設定の保存に失敗しました');
          setActionErrorMessage(message);
          showErrorToast(message);
        },
      }
    );
  };

  const handleFieldsSave = () => {
    setActionErrorMessage(null);
    const enabledFormFields: UpdateFormFieldItem[] = formFields
      .filter((field) => field.enabled)
      .map((field) => {
        if (field.field_type === 'fixed') {
          return {
            field_type: 'fixed',
            field_key: field.field_key,
            is_required: field.is_required,
          };
        }

        return {
          field_type: 'custom',
          custom_field_id: field.custom_field_id,
          is_required: field.is_required,
        };
      });

    updateFormFields.mutate(
      { form_fields: enabledFormFields },
      {
        onSuccess: (response) => {
          setFormFields(response.items);
          showSuccessToast('応募項目設定を保存しました');
        },
        onError: (error) => {
          const message = getErrorMessage(error, '応募項目設定の保存に失敗しました');
          setActionErrorMessage(message);
          showErrorToast(message);
        },
      }
    );
  };

  const isLoading = isSettingLoading || isFormFieldsLoading;
  const fetchErrorMessage = settingError
    ? getErrorMessage(settingError, 'アライアンス設定の取得に失敗しました')
    : formFieldsError
      ? getErrorMessage(formFieldsError, '応募項目設定の取得に失敗しました')
      : null;
  const errorMessage = actionErrorMessage || fetchErrorMessage;

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      {isLogoEditorOpen && logoOriginalUrl && (
        <AvatarEditorModal
          title="ロゴ画像をトリミング"
          circular={false}
          src={logoOriginalUrl}
          onClose={() => setIsLogoEditorOpen(false)}
          onSave={(croppedImage) => {
            setLogoPreviewUrl(croppedImage);
            const blob = base64ToBlob(croppedImage);
            uploadLogo.mutate(blob, {
              onSuccess: () => {
                showSuccessToast('ロゴを保存しました');
              },
              onError: (error) => {
                const message = getErrorMessage(error, 'ロゴの保存に失敗しました');
                setActionErrorMessage(message);
                showErrorToast(message);
              },
            });
          }}
        />
      )}

      <div className="flex flex-col gap-md">
        {errorMessage && (
          <div className="rounded border border-solid border-danger bg-red-50 p-sm">
            <p className="text-body-sm text-danger">{errorMessage}</p>
          </div>
        )}

        {/* 全般セクション */}
        <div className="flex flex-col gap-md rounded bg-white p-lg shadow-xs">
          <h2 className="text-heading-sm">全般</h2>

          {/* アライアンスサービスロゴ */}
          <div className="flex flex-col gap-xs">
            <h3 className="m-0 text-heading-xxs">ロゴ</h3>
            <div className="flex flex-col gap-xs">
              <div className="flex items-center gap-md">
                <div
                  className={`flex h-32 w-[270px] shrink-0 items-center justify-center rounded${!logoPreviewUrl ? 'border-2 border-solid border-border bg-gray' : ''}`}
                >
                  {logoPreviewUrl ? (
                    <img
                      src={logoPreviewUrl}
                      alt="プレビュー"
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <p className="text-body-xs text-sub">画像なし</p>
                  )}
                </div>
                <input
                  ref={logoFileInputRef}
                  id="alliance-logo-upload"
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="hidden"
                  onChange={handleLogoInputChange}
                />
                <Button
                  type="button"
                  color="secondary"
                  size="md"
                  onClick={() => logoFileInputRef.current?.click()}
                >
                  画像をアップロード
                </Button>
                {logoPreviewUrl && (
                  <Button
                    type="button"
                    color="danger-secondary"
                    size="md"
                    onClick={() => {
                      deleteLogo.mutate(undefined, {
                        onSuccess: () => {
                          setLogoPreviewUrl(null);
                          showSuccessToast('ロゴを削除しました');
                        },
                        onError: (error) => {
                          const message = getErrorMessage(error, 'ロゴの削除に失敗しました');
                          setActionErrorMessage(message);
                          showErrorToast(message);
                        },
                      });
                    }}
                    disabled={deleteLogo.isPending}
                  >
                    {deleteLogo.isPending ? '削除中...' : '画像を削除'}
                  </Button>
                )}
              </div>
              <p className="text-body-xs text-sub">
                5MB以下の画像ファイルをアップロードしてください
              </p>
            </div>
          </div>

          {/* アライアンスユーザー画面URL */}
          <div className="flex items-center gap-md">
            <div className="flex flex-col gap-xs">
              <h3 className="text-heading-xxs">アライアンスユーザー画面URL</h3>
              <div className="flex flex-col gap-xxs">
                <div className="flex items-center gap-xxs">
                  <p className="text-body-sm">
                    {allianceSetting?.app_base_url ?? DEFAULT_APP_BASE_URL}/alliance/
                  </p>
                  <Input
                    value={allianceUrl}
                    onChange={(e) => setAllianceUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button asChild color="tertiary" size="md" className="gap-xxs">
                    <a
                      href={`${allianceSetting?.app_base_url ?? DEFAULT_APP_BASE_URL}/alliance/${allianceUrl || ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      アライアンスユーザー画面へ
                      <MdOpenInNew size={16} />
                    </a>
                  </Button>
                </div>
                <p className="text-xs text-sub">
                  提携エージェントが使用するアライアンスユーザー画面のURLを設定
                </p>
              </div>
            </div>
          </div>

          {/* 利用規約URL */}
          <div className="w-[570px]">
            <div className="flex flex-col gap-xs">
              <h3 className="text-heading-xxs">利用規約URL</h3>
              <div className="flex flex-col gap-xxs">
                <Input
                  value={termsUrl}
                  onChange={(e) => setTermsUrl(e.target.value)}
                  className="w-full"
                  placeholder="https://example.com/terms"
                />
                <p className="text-body-xs text-sub">
                  アライアンスユーザー画面に表示される利用規約のURLを設定
                </p>
              </div>
            </div>
          </div>

          {/* 保存ボタン */}
          <div className="flex justify-end">
            <Button
              color="primary"
              size="md"
              onClick={handleGeneralSave}
              disabled={updateAllianceSetting.isPending}
            >
              {updateAllianceSetting.isPending ? '保存中...' : '保存する'}
            </Button>
          </div>
        </div>

        {/* 応募の項目設定セクション */}
        <div className="flex flex-col gap-md rounded bg-white p-lg shadow-layer1">
          <div className="flex flex-col gap-xs">
            <h2 className="text-heading-sm">応募の項目設定</h2>
            <p className="text-body-sm text-sub">
              アライアンス経由で応募時に求職者情報として入力してもらう項目を設定できます
            </p>
          </div>

          {/* フィールドリスト */}
          <div className="flex flex-col gap-xs">
            {formFields.map((field) => (
              <div
                key={getFieldId(field)}
                className="flex items-center justify-between rounded border border-solid border-border bg-white p-3"
              >
                <div className="flex items-center gap-xs">
                  <Checkbox
                    checked={field.enabled}
                    onChange={(checked) => handleFieldToggle(getFieldId(field), checked)}
                    disabled={field.enabled_locked}
                  />
                  <span className="text-sm">{field.name}</span>
                  {(field.required_locked || field.enabled_locked) && (
                    <Tag color={'danger'} size={'sm'}>
                      <span className="font-bold">必須</span>
                    </Tag>
                  )}
                </div>
                <div className="flex items-center gap-xs">
                  <Switch
                    checked={field.is_required}
                    onCheckedChange={(checked: boolean) =>
                      handleRequiredToggle(getFieldId(field), checked)
                    }
                    disabled={field.required_locked || field.enabled_locked}
                  />
                  <span className="text-body-xs">必須項目</span>
                </div>
              </div>
            ))}
          </div>

          {/* 保存ボタン */}
          <div className="flex justify-end">
            <Button
              color="primary"
              size="md"
              onClick={handleFieldsSave}
              disabled={updateFormFields.isPending}
            >
              {updateFormFields.isPending ? '保存中...' : '保存する'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
